import { useEffect, useState } from 'react';
import {
  getPublicProfile,
  initiatePayment,
  normalizePhoneNumber,
  verifyPayment,
} from './paymentApi';
import './App.css';

const presetTips = [
  { id: 'uji', label: 'Uji', amount: 50, caption: 'A warm cup for the road.' },
  { id: 'tea', label: 'Tea', amount: 100, caption: 'A thoughtful thank you.' },
  { id: 'fare', label: 'Fare', amount: 250, caption: 'Support the trip home.' },
  { id: 'lunch', label: 'Lunch', amount: 500, caption: 'A proper midday meal.' },
];

function readTipTokenFromPath() {
  if (typeof window === 'undefined') {
    return '';
  }

  const trimmedPath = decodeURIComponent(window.location.pathname)
    .replace(/^\/+|\/+$/g, '')
    .trim();

  const segments = trimmedPath.split('/').filter(Boolean);
  if (segments.length !== 2 || segments[0].toLowerCase() !== 't') {
    return '';
  }

  return segments[1].trim();
}

function readCurrentPath() {
  if (typeof window === 'undefined') {
    return '/';
  }

  return window.location.pathname || '/';
}

function normalizeRoute(path) {
  const normalizedPath = String(path || '/').replace(/\/+$/g, '') || '/';
  return normalizedPath.toLowerCase();
}

function isTipProfilePath(path) {
  const normalized = normalizeRoute(path).replace(/^\/+/, '');
  const segments = normalized.split('/').filter(Boolean);
  return segments.length === 2 && segments[0] === 't' && /^[A-Za-z0-9_-]{32}$/.test(segments[1]);
}

function setMetaContent(name, content) {
  if (typeof document === 'undefined') {
    return;
  }

  let tag = document.querySelector(`meta[name="${name}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function formatKes(amount) {
  return `Kes ${Number(amount).toLocaleString('en-KE')}`;
}

function getInitials(name) {
  if (!name) {
    return 'G';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function looksLikeImage(value) {
  return typeof value === 'string' && /^(https?:\/\/|data:|\/)/i.test(value);
}

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function extractCheckoutRequestID(response) {
  return (
    response?.checkoutRequestID ||
    response?.CheckoutRequestID ||
    response?.data?.checkoutRequestID ||
    response?.data?.CheckoutRequestID ||
    response?.response?.checkoutRequestID ||
    response?.response?.CheckoutRequestID ||
    ''
  );
}

function getResponseCode(response) {
  const rawCode =
    response?.ResponseCode ??
    response?.responseCode ??
    response?.data?.ResponseCode ??
    response?.data?.responseCode ??
    null;

  return rawCode === null || rawCode === undefined ? null : String(rawCode);
}

function getResultCode(response) {
  const rawCode =
    response?.ResultCode ??
    response?.resultCode ??
    response?.data?.ResultCode ??
    response?.data?.resultCode ??
    null;

  return rawCode === null || rawCode === undefined ? null : Number(rawCode);
}

function getResultDescription(response) {
  return (
    response?.ResultDesc ||
    response?.resultDesc ||
    response?.data?.ResultDesc ||
    response?.data?.resultDesc ||
    ''
  );
}

function getResponseMessage(response) {
  return (
    getResultDescription(response) ||
    response?.message ||
    response?.statusDescription ||
    response?.description ||
    response?.ResponseDescription ||
    response?.CustomerMessage ||
    response?.ResponseDescription ||
    response?.errorMessage ||
    ''
  );
}

function isPromptAccepted(response) {
  return getResponseCode(response) === '0' && Boolean(extractCheckoutRequestID(response));
}

function isVerificationPending(response) {
  const resultCode = getResultCode(response);
  const responseCode = getResponseCode(response);
  const resultDesc = getResultDescription(response).toLowerCase();

  if (responseCode === '0' && resultCode === null) {
    return true;
  }

  return (
    resultDesc.includes('being processed') ||
    resultDesc.includes('is processed') ||
    resultDesc.includes('processing') ||
    resultDesc.includes('pending') ||
    resultDesc.includes('awaiting')
  );
}

function isPaymentSuccessful(response) {
  const responseText = JSON.stringify(response || {}).toLowerCase();
  const resultCode = getResultCode(response);

  if (resultCode === 0) {
    return true;
  }

  return (
    getResultDescription(response).toLowerCase().includes('processed successfully') ||
    responseText.includes('payment received')
  );
}

function isPaymentFailed(response) {
  const responseText = JSON.stringify(response || {}).toLowerCase();
  const resultCode = getResultCode(response);

  return (
    (
      !isVerificationPending(response) &&
      resultCode !== null &&
      resultCode !== 0 &&
      !Number.isNaN(resultCode)
    ) ||
    responseText.includes('cancelled') ||
    responseText.includes('canceled') ||
    responseText.includes('failed') ||
    responseText.includes('insufficient') ||
    responseText.includes('expired') ||
    responseText.includes('timeout')
  );
}

async function sendPaymentPromptWithRetry({
  phone,
  amount,
  payerPhone,
  recipientUserId,
  recipientName,
  recipientPhoneNumber,
  rating,
  comment,
  onStatus,
}) {
  const attempts = 8;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    onStatus?.(
      attempt === 1
        ? 'Sending M-Pesa prompt...'
        : `Retrying M-Pesa prompt... attempt ${attempt} of ${attempts}.`
    );

    try {
      const paymentResponse = await initiatePayment({
        phone,
        amount,
        payerPhone,
        recipientUserId,
        recipientName,
        recipientPhoneNumber,
        rating,
        comment,
      });
      if (isPromptAccepted(paymentResponse)) {
        return {
          paymentResponse,
          checkoutRequestID: extractCheckoutRequestID(paymentResponse),
        };
      }
    } catch (error) {
      if (attempt === attempts) {
        throw error;
      }
    }

    if (attempt < attempts) {
      await delay(3000);
    }
  }

  throw new Error(
    'We could not send the M-Pesa prompt after several attempts. Please confirm your number and try again.'
  );
}

async function pollPaymentStatus(checkoutRequestID, onStatus) {
  const attempts = 24;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    onStatus?.(
      attempt === 0
        ? 'Prompt sent. Waiting for payment confirmation...'
        : `Still checking payment confirmation... attempt ${attempt + 1} of ${attempts}.`
    );

    if (attempt > 0) {
      await delay(5000);
    }

    let verificationResponse = null;

    try {
      verificationResponse = await verifyPayment(checkoutRequestID);
    } catch (error) {
      if (attempt === attempts - 1) {
        throw error;
      }

      continue;
    }

    if (isPaymentSuccessful(verificationResponse)) {
      return { state: 'success', response: verificationResponse };
    }

    if (isVerificationPending(verificationResponse)) {
      continue;
    }

    if (isPaymentFailed(verificationResponse)) {
      return { state: 'failed', response: verificationResponse };
    }
  }

  return { state: 'pending', response: null };
}

function ProgressDots() {
  return (
    <div className="progress-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );
}

function StarIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 3.6 2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.88l-5.2 2.73.99-5.79-4.21-4.1 5.82-.85L12 3.6Z" />
    </svg>
  );
}

function SuccessIcon() {
  return (
    <svg
      className="success-icon"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="30" />
      <path d="M19 33.5 27.5 42 45.5 23.5" />
    </svg>
  );
}

function PresetIcon({ id }) {
  const common = {
    width: 28,
    height: 28,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  if (id === 'uji') {
    return (
      <svg {...common}>
        <path d="M6 8h9v5a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V8Z" />
        <path d="M15 10h1.2a2.8 2.8 0 0 1 0 5.6H15" />
        <path d="M8 4c0 1 .7 1.5.7 2.5S8 8 8 8" />
        <path d="M11 4c0 1 .7 1.5.7 2.5S11 8 11 8" />
      </svg>
    );
  }

  if (id === 'tea') {
    return (
      <svg {...common}>
        <path d="M5 10h10v4a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4v-4Z" />
        <path d="M15 11h1.5a2.5 2.5 0 0 1 0 5H15" />
        <path d="M7 7h6" />
      </svg>
    );
  }

  if (id === 'fare') {
    return (
      <svg {...common}>
        <rect x="4.5" y="6.5" width="15" height="10" rx="2.5" />
        <path d="M7 16.5v1.5" />
        <path d="M17 16.5v1.5" />
        <path d="M4.5 10.5h15" />
        <circle cx="8" cy="13.5" r=".9" fill="currentColor" stroke="none" />
        <circle cx="16" cy="13.5" r=".9" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M4.5 11.5h15" />
      <path d="M6.5 11.5v4.5a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5v-4.5" />
      <path d="M8.5 11.5v-2a3.5 3.5 0 1 1 7 0v2" />
    </svg>
  );
}

const sitePages = {
  '/': {
    eyebrow: '',
    title: 'A clear, trusted way to tip waiters and service staff.',
    description:
      'Gratify helps customers send cashless tips to verified waiters, bartenders, hosts, riders, and other service staff using a QR code linked to their registered business profile.',
  },
  '/about': {
    eyebrow: 'About Gratify',
    title: 'Built for restaurants, hospitality teams, and the people who serve customers every day.',
    description:
      'Gratify is a Kenyan tipping platform that lets businesses register their staff, manage subscriptions, and give each team member a trusted tipping profile customers can scan.',
  },
  '/how-it-works': {
    eyebrow: 'How it works',
    title: 'Scan, confirm, tip, and leave a rating.',
    description:
      'Customers scan a staff member QR code, review the recipient profile, enter their M-Pesa number, choose a tip amount, and confirm the payment prompt on their phone.',
  },
  '/contact': {
    eyebrow: 'Contact',
    title: 'Questions about a tip, profile, or business account?',
    description:
      'Contact Gratify support for help with tipping links, waiter profiles, restaurant onboarding, business subscriptions, or payment confirmation questions.',
  },
  '/download-app': {
    eyebrow: 'Download app',
    title: 'Download the Gratify staff app.',
    description:
      'Get the Gratify mobile app for staff to create an account, join a business with a code, view tips, ratings, withdrawals, and share a QR tipping profile.',
  },
  '/delete-my-data': {
    eyebrow: 'Delete my data',
    title: 'Request deletion of your Gratify account or personal data.',
    description:
      'Use this page to request deletion of Gratify mobile app, tipping profile, support, or business account data.',
  },
  '/privacy': {
    eyebrow: 'Privacy',
    title: 'We collect only the details needed to process tips and support accounts.',
    description:
      'Gratify uses profile, contact, transaction, rating, and business subscription information to operate secure tipping pages and business dashboards.',
  },
  '/terms': {
    eyebrow: 'Terms',
    title: 'Use Gratify only for genuine service tips.',
    description:
      'Gratify tipping links are for voluntary appreciation payments to registered service staff. Businesses must keep active subscriptions for staff to receive tips.',
  },
};

const BUSINESS_APP_URL = 'https://gratify-business.web.app';
const STAFF_APP_DOWNLOAD_URL = 'https://play.google.com/store/apps/details?id=com.kiota.gratifyapp';

function SiteHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="site-header">
      <a className="site-brand" href="/" aria-label="Gratify home">
        <span className="site-brand-mark">G</span>
        <span>
          <strong>Gratify</strong>
          <small>Cashless tips for service staff</small>
        </span>
      </a>

      <button
        className={`site-menu-button${isMenuOpen ? ' active' : ''}`}
        type="button"
        aria-label="Toggle navigation menu"
        aria-expanded={isMenuOpen}
        aria-controls="site-navigation"
        onClick={() => setIsMenuOpen((current) => !current)}
      >
        <span />
        <span />
        <span />
      </button>

      <nav
        id="site-navigation"
        className={`site-nav${isMenuOpen ? ' open' : ''}`}
        aria-label="Main navigation"
      >
        <a href="/about" onClick={() => setIsMenuOpen(false)}>About</a>
        <a href="/how-it-works" onClick={() => setIsMenuOpen(false)}>How it works</a>
        <a href="/download-app" onClick={() => setIsMenuOpen(false)}>Download app</a>
        <a href="/contact" onClick={() => setIsMenuOpen(false)}>Contact</a>
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div>
        <strong>Gratify</strong>
        <p>QR tipping pages for waiters, bartenders, hospitality teams, and service staff.</p>
      </div>

      <nav aria-label="Footer navigation">
        <a href="/download-app">Download app</a>
        <a href="/delete-my-data">Delete my data</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
        <a href="/contact">Contact</a>
      </nav>
    </footer>
  );
}

function TipPageFooter() {
  return (
    <footer className="tip-page-footer" aria-label="Privacy and account links">
      <span>Gratify</span>
      <nav aria-label="Privacy and account links">
        <a href="/privacy">Privacy policy</a>
        <a href="/delete-my-data">Delete account/data</a>
      </nav>
    </footer>
  );
}

function MarketingHome() {
  return (
    <>
      <section className="site-hero">
        <div className="site-hero-copy">
          <h1>Tip waiters and service staff with a simple scan.</h1>
          <p>
            Gratify gives every registered staff member a profile and QR code, so customers can
            send a cashless tip, leave a rating, and know exactly who they are appreciating.
          </p>
          <div className="site-actions">
            <a className="site-primary-action" href={`${BUSINESS_APP_URL}/register`}>Business sign up</a>
            <a className="site-secondary-action" href={`${BUSINESS_APP_URL}/login`}>Business login</a>
            <a className="site-secondary-action" href="/download-app">Download staff app</a>
          </div>
        </div>

        <div className="site-hero-card" aria-label="Example Gratify tipping flow">
          <span>Example tip page</span>
          <strong>Jane Wanjiku</strong>
          <p>Waiter at a registered restaurant</p>
          <div className="site-qr-preview">
            <span />
            <span />
            <span />
            <span />
          </div>
          <small>Scan QR code. Confirm recipient. Send tip by M-Pesa.</small>
        </div>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <section className="site-content-card">
      <h1>About Gratify</h1>
      <p>
        Gratify is a tipping platform for restaurants, hotels, bars, salons, delivery teams, and
        other service businesses. Businesses create an account, subscribe, and invite staff using a
        business code. Staff then use the Gratify mobile app to view tips, ratings, withdrawals,
        and their personal QR code.
      </p>
      <p>
        When a customer scans a staff QR code, the public Gratify page confirms the staff profile
        and lets the customer send a voluntary tip. The business dashboard helps owners see team
        activity, average ratings, and customer feedback.
      </p>
    </section>
  );
}

function HowItWorksPage() {
  return (
    <section className="site-content-card">
      <h1>How Gratify tipping works</h1>
      <div className="site-steps">
        <article>
          <span>1</span>
          <h2>Business registers staff</h2>
          <p>A restaurant or service business creates an account, subscribes, and shares its code with staff.</p>
        </article>
        <article>
          <span>2</span>
          <h2>Staff shares QR code</h2>
          <p>The staff member opens the Gratify app and displays a QR code linked to their tipping profile.</p>
        </article>
        <article>
          <span>3</span>
          <h2>Customer sends a tip</h2>
          <p>The customer scans, checks the recipient, chooses an amount, and confirms the M-Pesa prompt.</p>
        </article>
        <article>
          <span>4</span>
          <h2>Tip and rating are recorded</h2>
          <p>The staff member sees the tip, rating, and comment in the app and can request withdrawals.</p>
        </article>
      </div>
    </section>
  );
}

function ContactPage() {
  return (
    <section className="site-content-card">
      <h1>Contact Gratify</h1>
      <p>
        Need help with a tip, staff QR code, business account, subscription, rating, or payment
        confirmation? Contact the Gratify team and include the staff name, business name, phone
        number used, and approximate transaction time where relevant.
      </p>
      <div className="site-contact-list">
        <a href="mailto:support@gratifytips.online">support@gratifytips.online</a>
        <a href="https://gratifytips.online">gratifytips.online</a>
      </div>
      <p className="site-note">
        Gratify support will never ask for your M-Pesa PIN, passwords, or full card details.
      </p>
    </section>
  );
}

function DownloadAppPage() {
  return (
    <section className="site-content-card">
      <h1>Download the Gratify staff app</h1>
      <p>
        The Gratify mobile app is for waiters, bartenders, hosts, riders, and other service staff.
        Staff can create an account, join a registered business using the business code, view tips,
        see ratings, request withdrawals, upload a profile picture, and share a QR code for tipping.
      </p>
      <div className="site-app-actions">
        <a className="site-primary-action" href={STAFF_APP_DOWNLOAD_URL}>
          Download on Google Play
        </a>
        <a className="site-secondary-action" href="/privacy">
          Read privacy policy
        </a>
      </div>
      <p className="site-note">
        If the Play Store listing is not live yet, this link will begin working after the app is
        approved and published under package name com.kiota.gratifyapp.
      </p>
    </section>
  );
}

function DeleteMyDataPage() {
  return (
    <section className="site-content-card">
      <h1>Delete my Gratify data</h1>
      <p>
        You can request deletion of personal data connected to your Gratify mobile app account,
        public tipping profile, customer tip activity, support request, or business account. This
        page is provided for app store compliance and for users who want a clear data deletion path.
      </p>

      <div className="privacy-section">
        <h2>How to request deletion</h2>
        <p>
          Email support@gratifytips.online using the subject “Delete my Gratify data”. Include the
          phone number or email used on Gratify, whether you are a staff member, customer, or
          business owner, and the business name or code if available.
        </p>
        <div className="site-app-actions">
          <a
            className="site-primary-action"
            href="mailto:support@gratifytips.online?subject=Delete%20my%20Gratify%20data"
          >
            Email deletion request
          </a>
          <a className="site-secondary-action" href="/privacy">
            View privacy policy
          </a>
        </div>
      </div>

      <div className="privacy-section">
        <h2>What can be deleted</h2>
        <ul>
          <li>Staff account profile information such as name, phone number, profile photo, and profile text.</li>
          <li>Mobile app account data connected to your Gratify login where deletion is legally and operationally allowed.</li>
          <li>Support messages and optional information you sent to Gratify support.</li>
          <li>Business account contact information where the requester is authorized to manage that business account.</li>
        </ul>
      </div>

      <div className="privacy-section">
        <h2>Information we may need to keep</h2>
        <p>
          Some records may be retained where needed for payment verification, withdrawal records,
          fraud prevention, accounting, dispute handling, legal obligations, security, or legitimate
          business operations. For example, transaction references, tip amounts, payment status, and
          audit records may need to remain even after a profile is removed.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Processing time</h2>
        <p>
          Gratify will review deletion requests and respond using the contact details provided. We
          may ask for reasonable verification before deleting data so that another person cannot
          delete your account without permission.
        </p>
      </div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <section className="site-content-card privacy-policy">
      <h1>Privacy policy</h1>
      <p className="site-updated">Last updated: June 2, 2026</p>

      <p>
        Gratify is a cashless tipping platform for registered waiters, bartenders, hospitality
        teams, delivery staff, and other service workers. This Privacy Policy explains how Gratify
        collects, uses, stores, and protects information when you use the Gratify website, public
        tipping pages, business dashboard, and mobile applications.
      </p>

      <div className="privacy-section">
        <h2>Who this policy applies to</h2>
        <p>
          This policy applies to customers who scan a Gratify QR code or send a tip, staff members
          who use the Gratify mobile app, business owners or managers who use the business portal,
          and anyone who contacts Gratify support.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Information we collect</h2>
        <ul>
          <li>Account information such as name, phone number, email address, password authentication data, business code, role, and business name.</li>
          <li>Staff profile information such as profile photo, job title, business link, public tipping profile, ratings, comments, tip history, and withdrawal requests.</li>
          <li>Business information such as business name, contact details, subscription status, staff list, business code, and dashboard activity.</li>
          <li>Tip and transaction information such as tip amount, recipient, payer phone number, M-Pesa checkout request or receipt reference, payment status, timestamps, rating, and customer comment.</li>
          <li>Support information such as messages you send to us, issue descriptions, screenshots you choose to share, and contact details for follow-up.</li>
          <li>Technical information such as device type, app version, browser type, approximate logs, error information, and basic security events needed to keep the service reliable.</li>
        </ul>
      </div>

      <div className="privacy-section">
        <h2>Mobile app permissions</h2>
        <p>
          The Gratify mobile app may request permissions only when they are needed for app features.
          You can manage permissions from your device settings.
        </p>
        <ul>
          <li>Camera or photo access may be used when you upload or update your profile picture.</li>
          <li>Storage or media access may be used to select a profile image from your device.</li>
          <li>Notification permission may be used to alert staff when a tip is received or when account activity needs attention.</li>
          <li>Internet access is required for login, profile loading, QR code links, tips, ratings, withdrawals, and syncing data with Gratify services.</li>
        </ul>
      </div>

      <div className="privacy-section">
        <h2>How we use information</h2>
        <ul>
          <li>To create and manage staff, business, and customer-facing tipping profiles.</li>
          <li>To validate business codes and confirm that a business subscription is active before staff can receive tips.</li>
          <li>To process tips, verify payment status, record transactions, send confirmations, and show tip history.</li>
          <li>To display staff ratings, comments, dashboard summaries, and business reporting.</li>
          <li>To support withdrawals, account updates, fraud prevention, troubleshooting, and customer support.</li>
          <li>To improve the app, fix errors, protect accounts, prevent misuse, and comply with applicable legal or regulatory obligations.</li>
        </ul>
      </div>

      <div className="privacy-section">
        <h2>Payments and sensitive information</h2>
        <p>
          Gratify does not ask for your M-Pesa PIN, card PIN, banking password, or banking login
          details. Customers confirm payments through the supported payment prompt on their own
          phone. Gratify may store payment references, amounts, recipient details, payer phone
          numbers, and payment status so tips can be confirmed and shown in staff and business
          dashboards.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Sharing information</h2>
        <p>
          Gratify does not sell personal information. We may share limited information with service
          providers that help us operate the platform, including hosting, authentication, database,
          storage, SMS, notification, payment processing, analytics, and support providers. We may
          also share information if required by law, to prevent fraud or abuse, to protect users, or
          to resolve payment and support issues.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Public tipping profiles</h2>
        <p>
          Staff tipping profiles are intended to be visible to customers who scan a QR code. A
          public profile may show the staff member name, profile photo or initials, basic profile
          text, and tipping form. Customers should only see enough information to confirm they are
          tipping the correct registered staff member.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Data storage and security</h2>
        <p>
          Gratify uses reasonable technical and organizational safeguards to protect user data,
          including authenticated access, hosted database services, restricted app flows, and
          subscription checks before tips can be accepted. No online service can guarantee absolute
          security, but we work to protect accounts, payment records, and profile information from
          unauthorized access, loss, misuse, or alteration.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Data retention</h2>
        <p>
          We keep account, business, transaction, rating, and withdrawal information for as long as
          needed to provide Gratify, meet accounting or operational requirements, resolve disputes,
          prevent fraud, and comply with legal obligations. We may retain transaction records even
          after an account is closed where needed for financial, security, or compliance reasons.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Your choices and rights</h2>
        <ul>
          <li>You may update your staff profile information in the Gratify mobile app where editing is available.</li>
          <li>You may request help correcting, deleting, or accessing your information by using the Delete My Data page or contacting Gratify support.</li>
          <li>You may disable app notifications from your device settings.</li>
          <li>You may choose not to upload a profile photo, although some profile features may be less personalized.</li>
          <li>You may stop using Gratify at any time, subject to any outstanding business, withdrawal, payment, or compliance matters.</li>
        </ul>
      </div>

      <div className="privacy-section">
        <h2>Children</h2>
        <p>
          Gratify is not intended for children. Staff accounts should be created only by people who
          are allowed to work with the registered business and use tipping or payment services under
          applicable law.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Changes to this policy</h2>
        <p>
          We may update this Privacy Policy as Gratify changes. When we make material changes, we
          will update the date above and may provide additional notice through the website, app, or
          business dashboard.
        </p>
      </div>

      <div className="privacy-section">
        <h2>Contact us</h2>
        <p>
          For privacy questions, account support, data requests, or Play Store privacy inquiries,
          contact Gratify at support@gratifytips.online.
        </p>
      </div>
    </section>
  );
}

function TermsPage() {
  return (
    <section className="site-content-card">
      <h1>Terms of use</h1>
      <p>
        Gratify tipping links are for voluntary appreciation payments to registered service staff.
        Customers should confirm the staff profile, amount, and payment prompt before completing a
        tip. Businesses are responsible for registering genuine staff and keeping subscriptions
        active.
      </p>
      <p>
        Staff under inactive or expired business subscriptions cannot receive tips through Gratify.
        Gratify may block suspicious profiles, invalid business codes, or activity that appears
        fraudulent, misleading, or unrelated to genuine service tipping.
      </p>
      <p>
        Gratify is not a bank and does not provide banking login services. Payment confirmation is
        handled through the supported payment flow shown on the tipping page.
      </p>
    </section>
  );
}

function NotFoundPage() {
  return (
    <section className="site-content-card">
      <h1>Page not found</h1>
      <p>
        This page is not available. If you scanned a Gratify QR code, please check that the link
        begins with /t/ followed by the staff profile token.
      </p>
      <a className="site-primary-action site-inline-action" href="/">Go to Gratify home</a>
    </section>
  );
}

function MarketingSite({ routePath }) {
  const route = normalizeRoute(routePath);
  const page = sitePages[route] || {
    eyebrow: 'Gratify',
    title: 'Page not found',
    description: 'Learn about Gratify, a cashless tipping platform for waiters and service staff.',
  };

  return (
    <div className="app-shell site-shell">
      <div className="background-orb background-orb-left" />
      <div className="background-orb background-orb-right" />
      <SiteHeader />

      <main className="site-main">
        {route !== '/' ? (
          <section className="site-page-intro" aria-label={page.eyebrow}>
            <span>{page.eyebrow}</span>
            <h1>{page.title}</h1>
            <p>{page.description}</p>
          </section>
        ) : null}

        {route === '/' ? <MarketingHome /> : null}
        {route === '/about' ? <AboutPage /> : null}
        {route === '/how-it-works' ? <HowItWorksPage /> : null}
        {route === '/contact' ? <ContactPage /> : null}
        {route === '/download-app' ? <DownloadAppPage /> : null}
        {route === '/delete-my-data' ? <DeleteMyDataPage /> : null}
        {route === '/privacy' ? <PrivacyPage /> : null}
        {route === '/terms' ? <TermsPage /> : null}
        {!sitePages[route] ? <NotFoundPage /> : null}
      </main>

      <SiteFooter />
    </div>
  );
}

function App() {
  const routePath = readCurrentPath();
  const isTipRoute = isTipProfilePath(routePath);
  const tipToken = readTipTokenFromPath();
  const [recipient, setRecipient] = useState(null);
  const [loading, setLoading] = useState(Boolean(isTipRoute && tipToken));
  const [error, setError] = useState('');
  const [selectedAmount, setSelectedAmount] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitState, setSubmitState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfulPayment, setSuccessfulPayment] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRecipient() {
      if (!isTipRoute) {
        setLoading(false);
        setRecipient(null);
        setError('');
        return;
      }

      if (!tipToken) {
        setLoading(false);
        setRecipient(null);
        setError('This Gratify tipping link is incomplete. Please scan a valid staff QR code.');
        return;
      }

      setLoading(true);
      setError('');
      setSubmitState(null);
      setSuccessfulPayment(null);
      setRating(0);
      setComment('');

      try {
        const nextRecipient = await getPublicProfile(tipToken);
        if (cancelled) return;
        setRecipient(nextRecipient);
      } catch (fetchError) {
        if (!cancelled) {
          setRecipient(null);
          setError(fetchError?.message || 'The profile could not be loaded right now. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRecipient();

    return () => {
      cancelled = true;
    };
  }, [isTipRoute, tipToken]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const route = normalizeRoute(routePath);
    const publicPage = sitePages[route];
    const defaultDescription =
      'Gratify is a cashless tipping platform for waiters, bartenders, hospitality teams, and service staff. Scan a verified QR code to send a voluntary tip and rating.';

    if (!isTipRoute) {
      document.title = publicPage
        ? `${publicPage.title} | Gratify`
        : 'Gratify | Cashless tips for service staff';
      setMetaContent('description', publicPage?.description || defaultDescription);
      setMetaContent(
        'keywords',
        'Gratify, tipping app, waiter tips, restaurant tipping, cashless tips, QR code tipping, M-Pesa tips, service staff tips'
      );
      return;
    }

    document.title = recipient
      ? `Tip ${recipient.FullName} | Gratify`
      : 'Gratify | Tip a verified service worker';
    setMetaContent(
      'description',
      recipient
        ? `Send a voluntary cashless tip and rating to ${recipient.FullName} through Gratify, a tipping platform for registered service staff.`
        : defaultDescription
    );
  }, [isTipRoute, recipient, routePath]);

  const activeAmount = Number(selectedAmount) || 0;

  function handlePresetSelect(amount) {
    setSelectedAmount(String(amount));
    setSubmitState(null);
  }

  function handleStartAnotherTip() {
    setSuccessfulPayment(null);
    setSubmitState(null);
    setIsSubmitting(false);
    setSelectedAmount('');
    setSenderPhone('');
    setRating(0);
    setComment('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!recipient) {
      setSubmitState({
        type: 'error',
        message: 'Open a Gratify profile first before preparing a tip.',
      });
      return;
    }

    if (!senderPhone.trim()) {
      setSubmitState({
        type: 'error',
        message: 'Enter your phone number so the service provider knows who sent the tip.',
      });
      return;
    }

    const normalizedPhone = normalizePhoneNumber(senderPhone);

    if (!/^254\d{9}$/.test(normalizedPhone)) {
      setSubmitState({
        type: 'error',
        message: 'Enter a valid M-Pesa number like 0712345678 or 254712345678.',
      });
      return;
    }

    if (!activeAmount || activeAmount < 1) {
      setSubmitState({
        type: 'error',
        message: 'Choose one of the tip boxes or type a custom amount above zero.',
      });
      return;
    }

    setIsSubmitting(true);
    setSuccessfulPayment(null);
    setSubmitState({
      type: 'pending',
      message: `Preparing your M-Pesa prompt for ${formatKes(activeAmount)}.`,
    });

    try {
      const { paymentResponse, checkoutRequestID } = await sendPaymentPromptWithRetry({
        phone: normalizedPhone,
        amount: activeAmount,
        payerPhone: normalizedPhone,
        recipientUserId: recipient?.userId || recipient?.id || '',
        recipientName: recipient?.FullName || '',
        recipientPhoneNumber: recipient?.phoneNumber || '',
        rating,
        comment,
        onStatus: (message) => {
          setSubmitState({
            type: 'pending',
            message: `${message} Check your phone and enter your PIN when the prompt arrives.`,
          });
        },
      });

      setSubmitState({
        type: 'pending',
        message:
          getResponseMessage(paymentResponse) ||
          'M-Pesa prompt sent. Complete the payment on your phone. We are verifying the transaction now.',
      });

      const verification = await pollPaymentStatus(checkoutRequestID, (message) => {
        setSubmitState({
          type: 'pending',
          message,
        });
      });

      if (verification.state === 'success') {
        const successMessage =
          getResponseMessage(verification.response) ||
          `Payment received. ${formatKes(activeAmount)} has been confirmed for ${recipient.FullName}.`;
        const notificationMessage = verification.response?.tip?.notificationMessage ||
          'Payment confirmed and transaction recorded. SMS notifications are being processed.';

        setSuccessfulPayment({
          amount: activeAmount,
          recipientName: recipient.FullName,
          senderPhone,
          message: `${successMessage} ${notificationMessage}`,
          notificationMessage,
        });
        setSubmitState({
          type: 'success',
          message: successMessage,
        });
        return;
      }

      if (verification.state === 'failed') {
        setSubmitState({
          type: 'error',
          message:
            getResponseMessage(verification.response) ||
            'The payment was not completed. Please try again from the M-Pesa prompt.',
        });
        return;
      }

      setSubmitState({
        type: 'pending',
        message:
          'We are still waiting for confirmation from M-Pesa. If you completed the prompt, please wait a moment and try again.',
      });
    } catch (paymentError) {
      setSubmitState({
        type: 'error',
        message:
          paymentError?.message ||
          'We could not start the payment right now. Please try again shortly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isTipRoute) {
    return <MarketingSite routePath={routePath} />;
  }

  return (
    <div className="app-shell">
      <div className="background-orb background-orb-left" />
      <div className="background-orb background-orb-right" />

      <main className="layout simple-layout">
        <section className="tip-card simple-tip-card">
          {loading ? (
            <div className="state-card">
              <div className="loader" />
              <p>Loading profile details from Firestore...</p>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="state-card error-card">
              <p>{error}</p>
              <span>Try opening a real profile path such as `/@0714870528`.</span>
            </div>
          ) : null}

          {!loading && !error && successfulPayment ? (
            <div className="success-page">
              <SuccessIcon />
              <h1>Thank you.</h1>
              <p>
                Your tip of {formatKes(successfulPayment.amount)} for{' '}
                {successfulPayment.recipientName} has been received.
              </p>
              <span className="success-caption">{successfulPayment.message}</span>
              <button
                className="submit-button simple-submit-button success-action"
                type="button"
                onClick={handleStartAnotherTip}
              >
                Send another tip
              </button>
            </div>
          ) : null}

          {!loading && !error && !successfulPayment ? (
            <form className="tip-form simple-tip-form" onSubmit={handleSubmit}>
              <div className="simple-profile-header">
                <div className="avatar-wrap simple-avatar-wrap">
                  {looksLikeImage(recipient?.profilePic) ? (
                    <img
                      className="avatar-image simple-avatar-image"
                      src={recipient.profilePic}
                      alt={recipient.FullName || 'Recipient profile'}
                    />
                  ) : (
                    <div className="avatar-fallback simple-avatar-fallback">
                      {getInitials(recipient?.FullName)}
                    </div>
                  )}
                </div>

                <div className="profile-copy simple-profile-copy">
                  <h1>{recipient?.FullName || 'Gratify user'}</h1>
                  <p>{recipient?.aboutMe || 'Humbled by your support.'}</p>
                </div>
              </div>

              <label className="field-group simple-field-group">
                <span>
                  Your Mpesa number. <em>*</em>
                </span>
                <input
                  type="tel"
                  placeholder="07XXXXXXXX"
                  value={senderPhone}
                  disabled={isSubmitting}
                  onChange={(event) => setSenderPhone(event.target.value)}
                />
              </label>

              <div className="feedback-panel">
                <div className="feedback-heading">
                  <span>Rate this profile</span>
                  <small>Optional</small>
                </div>

                <div
                  className="star-rating"
                  role="radiogroup"
                  aria-label="Rate this profile from 1 to 5 stars"
                >
                  {[1, 2, 3, 4, 5].map((value) => {
                    const isActive = value <= rating;

                    return (
                      <button
                        key={value}
                        type="button"
                        className={`star-button${isActive ? ' active' : ''}`}
                        aria-label={`${value} star${value > 1 ? 's' : ''}. Tap again to clear.`}
                        aria-checked={rating === value}
                        role="radio"
                        disabled={isSubmitting}
                        onClick={() => {
                          setRating((currentRating) => (currentRating === value ? 0 : value));
                          setSubmitState(null);
                        }}
                      >
                        <StarIcon filled={isActive} />
                      </button>
                    );
                  })}
                </div>

                <label className="field-group feedback-comment-field">
                  <span>Comment</span>
                  <textarea
                    rows="4"
                    placeholder="Share a quick note about your experience..."
                    value={comment}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setComment(event.target.value);
                      setSubmitState(null);
                    }}
                  />
                </label>
              </div>

              <div className="tip-actions-row">
                <div className="preset-grid simple-preset-grid">
                  {presetTips.slice(1).map((tip) => {
                    const isActive = activeAmount === tip.amount;

                    return (
                      <button
                        key={tip.id}
                        type="button"
                        className={`preset-card simple-preset-card${isActive ? ' active' : ''}`}
                        disabled={isSubmitting}
                        onClick={() => handlePresetSelect(tip.amount)}
                      >
                        <span className="icon-badge simple-icon-badge">
                          <PresetIcon id={tip.id} />
                        </span>
                        <span className="simple-preset-text">
                          <strong>{tip.label}</strong>
                          <small>{tip.amount}/=</small>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="other-amount-wrap">
                  <label className="other-amount-field">
                    <span className="icon-badge other-icon-badge">G</span>
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Enter other amount"
          value={selectedAmount}
          disabled={isSubmitting}
                      onChange={(event) => {
                        setSelectedAmount(event.target.value);
                        setSubmitState(null);
                      }}
                    />
                  </label>

                  <button
                    className="submit-button simple-submit-button"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Checking...' : 'Tip'}
                  </button>
                </div>
              </div>

              <p className="amount-hint">
                Choose a quick tip box above, or type a custom amount in "Other Amount".
              </p>

              {submitState ? (
                submitState.type === 'pending' ? (
                  <div className="progress-panel" role="status" aria-live="polite">
                    <ProgressDots />
                    <div className="progress-copy">
                      <strong>Processing payment</strong>
                      <span>{submitState.message}</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`submit-banner ${
                      submitState.type === 'success' ? 'success-banner' : 'error-banner'
                    }`}
                  >
                    {submitState.message}
                  </div>
                )
              ) : null}
            </form>
          ) : null}
        </section>
      </main>

      <TipPageFooter />
    </div>
  );
}

export default App;
